'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(null);
  const supabase = createClient();

  const fetchSubscriptionStatus = async (userId: string) => {
    try {
      const { data } = await supabase
        .from('user_profiles')
        .select('subscription_status, role')
        .eq('id', userId)
        .single();
      // Admins and peak_psych users are always treated as active (free access)
      if (data?.role === 'admin' || data?.role === 'peak_psych') {
        setSubscriptionStatus('active');
      } else {
        setSubscriptionStatus(data?.subscription_status ?? 'inactive');
      }
    } catch {
      setSubscriptionStatus('inactive');
    }
  };

  // Silently stamp last_active for the signed-in user — fire-and-forget
  const updateLastActive = async (userId: string) => {
    try {
      await supabase
        .from('user_profiles')
        .update({ last_active: new Date().toISOString() })
        .eq('id', userId);
    } catch {
      // non-fatal — ignore errors
    }
  };

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user?.id) {
        fetchSubscriptionStatus(session.user.id).finally(() => setLoading(false));
        // Stamp last_active on page load (existing session)
        updateLastActive(session.user.id);
      } else {
        setLoading(false);
      }
    });

    // Listen for auth changes
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user?.id) {
        fetchSubscriptionStatus(session.user.id).finally(() => setLoading(false));
        // Stamp last_active whenever the user signs in or their token refreshes
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          updateLastActive(session.user.id);
        }
      } else {
        setSubscriptionStatus(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Email/Password Sign Up
  const signUp = async (email: string, password: string, metadata = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: (metadata as any)?.fullName || '',
          avatar_url: (metadata as any)?.avatarUrl || ''
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`
      }
    });
    if (error) throw error;
    return data;
  };

  // Email/Password Sign In
  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    if (error) throw error;
    return data;
  };

  // Sign Out
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    // "Auth session missing" means the user is already signed out — treat as success
    if (error && !error.message.includes('Auth session missing')) throw error;
  };

  // Get Current User
  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  // Check if Email is Verified
  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  // Get User Profile from Database
  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  };

  // Refresh subscription status (call after successful payment)
  const refreshSubscriptionStatus = async () => {
    if (user?.id) {
      await fetchSubscriptionStatus(user.id);
    }
  };

  const value = {
    user,
    session,
    loading,
    subscriptionStatus,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    refreshSubscriptionStatus,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
